import * as React from 'react';
import Drawer from 'expo-router/drawer';
import { DrawerToggleButton } from '@react-navigation/drawer';
import WorkedTable from '@/components/timesheets/worked-table';
import { ActivityIndicator, RefreshControl, ScrollView, View, Text, TouchableOpacity, StyleSheet, SafeAreaView } from 'react-native';
import TurnedDownOutsideTable from '@/components/timesheets/turned-down-outside-table';
import TurnedDownInsideTable from '@/components/timesheets/turned-down-inside-table';
import TimesheetRangeSelect from '@/components/timesheets/timesheet-range-select';
import { useCallback, useState } from 'react';
import { trpc } from '@/trpc/react';
import { useCrew } from '@/context/CrewProvider';
import { useSettings } from '@/hooks/useSettings';
import { endOfMonth, startOfMonth } from 'date-fns';
import { mapJobTimesheetToFrontend, mapPaymentsToFrontend, mapTurnedDownToFrontend } from '@/components/timesheets/types';
import { CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui';
import * as FileSystem from "expo-file-system/legacy";
import * as Sharing from 'expo-sharing';
import { formatDateForPrinting } from 'shared-utils';
import { useRefetchOnFocus } from '@/hooks/useRefetchOnFocus';
import colors from '@/constants/Colors';
import { Ionicons } from '@expo/vector-icons';
import PaymentTable from '@/components/timesheets/payment-table';

type TabType = "worked" | "turnedDown" | "payments";

export default function Timesheets() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tab, setTab] = useState<TabType>("worked");

  const { currentCrewMember } = useCrew();
  const extraHoursRate = useSettings('extra-hours')
  const plInsuranceRate = useSettings('pl-insurance-rate')

  // Common query options to prevent excessive refetching
  const queryOptions = {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  };

  const [{ data: timesheetData, isLoading: timesheetLoading, refetch: timesheetRefetch },
    { data: additionsData, isLoading: additionLoading, refetch: additionsRefetch },
    { data: deductionsData, isLoading: deductionsLoading, refetch: deductionsRefetch },
    { data: strikesData, isLoading: strikesDataLoading, refetch: strikesRefetch },
    { data: turnedDownOutsideData, isLoading: turnedDownOutsideLoading, refetch: turnedDownOutsideRefetch },
    { data: turnedDownInsideData, isLoading: turnedDownInsideLoading, refetch: turnedDownInsideRefetch },
    { data: plInsuranceSkillData, isLoading: plInsuranceSkillLoading, refetch: plInsuranceSkillRefetch },
    { data: paymentsData, isLoading: paymentsLoading, refetch: paymentsRefetch }
  ] = trpc.useQueries((t) => [
    t.jobTimesheet.listForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate) }, queryOptions),
    t.jobTimesheet.listAdditionsForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate) }, queryOptions),
    t.jobTimesheet.listDeductionsForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate) }, queryOptions),
    t.jobTimesheet.listStrikesForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate) }, queryOptions),
    t.jobTimesheet.listTurnedDownForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate), inside24Hours: false }, queryOptions),
    t.jobTimesheet.listTurnedDownForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate), inside24Hours: true }, queryOptions),
    t.jobTimesheet.getPLInsuranceSkillForCrew(currentCrewMember!.id, queryOptions),
    t.jobTimesheet.listPaymentsForCrew({ id: currentCrewMember!.id, fromDate: startOfMonth(selectedDate), toDate: endOfMonth(selectedDate) }, queryOptions)
  ]);

  const isFlatRate = currentCrewMember?.crew_levels?.[0]?.level?.can_provide_feedback === true

  const mappedTimesheet = timesheetData && additionsData && deductionsData && strikesData && plInsuranceSkillData ? mapJobTimesheetToFrontend(timesheetData, extraHoursRate, additionsData, deductionsData, strikesData, plInsuranceRate, plInsuranceSkillData?.data) : null;
  const mappedTurnedDown = turnedDownOutsideData ? mapTurnedDownToFrontend(turnedDownOutsideData.data, extraHoursRate, isFlatRate) : null;
  const mappedTurnedDownInside = turnedDownInsideData ? mapTurnedDownToFrontend(turnedDownInsideData.data, extraHoursRate, isFlatRate) : null;
  const mappedPayments = paymentsData ? mapPaymentsToFrontend(paymentsData.data) : null;

  const { mutate: downloadTimesheet } = trpc.jobTimesheet.downloadTimesheet.useMutation({
    async onSuccess(response) {
      const doc = response.data;

      const filePath = FileSystem.documentDirectory + doc.fileName;

      await FileSystem.writeAsStringAsync(filePath, doc.pdfBuffer, { encoding: FileSystem.EncodingType.Base64 });

      const shareable = await Sharing.isAvailableAsync();
      if (shareable) {
        await Sharing.shareAsync(filePath);
      }

      return filePath;
    },
  });

  const onDownload = () => {
    if (!mappedTimesheet || !mappedTurnedDown || !mappedTurnedDownInside) return;
    downloadTimesheet({
      mappedTimesheet: mappedTimesheet,
      mappedTurnedDown: mappedTurnedDown,
      mappedTurnedDownInside: mappedTurnedDownInside,
      crewName: currentCrewMember?.name || '',
      fromDate: formatDateForPrinting(startOfMonth(selectedDate)),
      toDate: formatDateForPrinting(endOfMonth(selectedDate)),
    })
  }

  const isLoading = timesheetLoading || additionLoading || deductionsLoading || strikesDataLoading || turnedDownOutsideLoading || turnedDownInsideLoading;

  const refetch = useCallback(() => {
    timesheetRefetch();
    additionsRefetch();
    deductionsRefetch();
    strikesRefetch();
    turnedDownOutsideRefetch();
    turnedDownInsideRefetch();
  }, [timesheetRefetch, additionsRefetch, deductionsRefetch, strikesRefetch, turnedDownOutsideRefetch, turnedDownInsideRefetch]);

  // Only refetch on focus if it's been more than 5 minutes since last fetch
  useRefetchOnFocus(refetch, true);

  if (isLoading) {
    return (
      <>
        <Drawer.Screen
          options={{
            drawerLabel: 'Timesheets',
            title: 'Timesheets',
            headerStyle: {
              backgroundColor: colors.primary,
            },
            headerTintColor: colors.accent,
            headerLeft: () => <DrawerToggleButton tintColor={colors.accent} />
          }}
        />
        <View className="flex-1 justify-center">
          <ActivityIndicator />
        </View>
      </>
    )
  }

  if (!mappedTimesheet || !mappedTurnedDown || !mappedTurnedDownInside) {
    return (
      <View className="flex-1 items-center justify-center">
        <Text>Nothing to show</Text>
      </View>
    )
  }

  const renderWorkedTab = () => (
    <>
      <CardTitle className='mt-6'>Worked</CardTitle>
      <WorkedTable timesheetData={mappedTimesheet} />
      <Button
        size="default"
        variant="crew"
        className="mb-10 mt-4"
        onPress={onDownload}
      >
        Download PDF
      </Button>
    </>
  );

  const renderTurnedDownTab = () => (
    <>
      <CardTitle className='mt-6'>Turned Down Jobs outside 24 hours</CardTitle>
      <TurnedDownOutsideTable timesheetData={mappedTurnedDown} />
      <CardTitle className='mt-3'>Turned Down Jobs inside 24 hours</CardTitle>
      <TurnedDownInsideTable timesheetData={mappedTurnedDownInside} />
      <Button
        size="default"
        variant="crew"
        className="mb-10 mt-4"
        onPress={onDownload}
      >
        Download PDF
      </Button>
    </>
  );

  const renderPaymentsTab = () => (
    <>
      <CardTitle className='mt-6'>Payments</CardTitle>
      <PaymentTable paymentData={mappedPayments ?? []} />
    </>
  );

  return (
    <SafeAreaView style={styles.container}>
      <Drawer.Screen
        options={{
          drawerLabel: 'Timesheets',
          title: 'Timesheets',
          headerStyle: {
            backgroundColor: colors.primary,
          },
          headerTintColor: colors.accent,
          headerLeft: () => <DrawerToggleButton tintColor={colors.accent} />
        }}
      />

      <View className="px-4 mt-6">
        <TimesheetRangeSelect onValueChange={setSelectedDate} selectedDate={selectedDate} />
      </View>

      {/* Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, tab === "worked" && styles.activeTab]}
          onPress={() => setTab("worked")}
        >
          <Ionicons
            name={tab === "worked" ? "briefcase" : "briefcase-outline"}
            size={20}
            color={tab === "worked" ? colors.accent : "#666"}
          />
          <Text style={[styles.tabText, tab === "worked" && styles.activeTabText]}>Worked</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, tab === "turnedDown" && styles.activeTab]}
          onPress={() => setTab("turnedDown")}
        >
          <Ionicons
            name={tab === "turnedDown" ? "close-circle" : "close-circle-outline"}
            size={20}
            color={tab === "turnedDown" ? colors.accent : "#666"}
          />
          <Text style={[styles.tabText, tab === "turnedDown" && styles.activeTabText]}>Turned Down</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, tab === "payments" && styles.activeTab]}
          onPress={() => setTab("payments")}
        >
          <Ionicons
            name={tab === "payments" ? "cash" : "cash-outline"}
            size={20}
            color={tab === "payments" ? colors.accent : "#666"}
          />
          <Text style={[styles.tabText, tab === "payments" && styles.activeTabText]}>Payments</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        className="bg-background"
        refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
        contentContainerStyle={{ padding: 16 }}
      >
        {tab === "worked" && renderWorkedTab()}
        {tab === "turnedDown" && renderTurnedDownTab()}
        {tab === "payments" && renderPaymentsTab()}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  tabContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: '#fff',
    paddingVertical: 4,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 12,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  activeTab: {
    backgroundColor: '#222',
  },
  tabText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: colors.accent,
    fontWeight: '600',
  },
});